import styled, { css } from 'styled-components';
import { mobile } from 'styles/breakpoints';
import { SIDEBAR_WIDTH } from 'components/Sidebar/styles';
import { pageWidthStyles, PAGE_CONTENT } from 'styles/elements';
import Link from 'components/Link';
import EmailLink from 'components/EmailLink';

const sidebarRightStyles = css`
  margin-right: ${SIDEBAR_WIDTH}px;

  ${mobile()} {
    margin-left: 0;
    margin-right: 0;
  }
`;

const sidebarLeftStyles = css`
  margin-left: ${SIDEBAR_WIDTH}px;

  ${mobile()} {
    margin-left: 0;
    margin-right: 0;
  }
`;

export const Wrapper = styled.footer<{ sidebar?: boolean; right?: boolean }>`
  background-color: ${({ theme }) => theme.color.backgroundSecondary};
  color: ${({ theme }) => theme.color.textSecondary};
  display: flex;
  flex-direction: column;
  font-weight: 400;
  ${({ sidebar, right }) => (sidebar ? (right ? sidebarRightStyles : sidebarLeftStyles) : null)};
  padding: 56px 2% 32px;
  scroll-margin-top: 88px;

  ${mobile()} {
    padding-top: 40px;
  }
`;

export const Content = styled.div`
  ${pageWidthStyles};
  max-width: ${PAGE_CONTENT}px;
  padding: 0 0 32px;
  text-align: left;
  display: flex;

  &:last-of-type {
    padding-bottom: 0;
  }

  ${mobile()} {
    flex-direction: column;
    padding-bottom: 24px;
  }
`;

export const GridContent = styled(Content)`
  order: 1;

  ${mobile()} {
    border-top: 1px solid rgba(255, 255, 255, 0.1);
    order: 3;
    padding-top: 24px;
  }
`;

export const TrustContent = styled(Content)`
  align-items: center;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  display: grid;
  gap: 24px;
  grid-template-columns: minmax(0, 1fr) minmax(360px, 0.9fr);
  order: 2;
  padding: 20px 0;

  @media (max-width: 1080px) {
    grid-template-columns: 1fr;
  }
`;

export const LegalContent = styled(Content)`
  order: 4;
`;

export const Disclaimer = styled.div`
  font-weight: 300;
  color: #c2c3c5;
  font-size: 13px;
  margin: 0;
  text-align: left;
  line-height: 20px;
  width: 100%;

  strong {
    font-weight: 700;
  }
`;

export const FooterLogo = styled.div`
  svg {
    height: 40px;
  }
`;

export const Copyright = styled.div`
  color: ${({ theme }) => theme.color.textPrimary};
  font-size: 14px;
  font-weight: 600;
`;

export const LinkGrid = styled.nav`
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  column-gap: 28px;
  row-gap: 34px;
  width: 100%;

  & > section:first-child {
    grid-column: span 3;
  }

  @media (max-width: 1180px) {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  @media (max-width: 760px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));

    & > section:first-child {
      grid-column: 1 / -1;
    }
  }

  ${mobile()} {
    grid-template-columns: 1fr;
    row-gap: 22px;

    & > section:first-child {
      grid-column: 1 / -1;
    }
  }
`;

export const LinkGroup = styled.section``;

export const LinkGroupTitle = styled.h2`
  color: ${({ theme }) => theme.color.textPrimary};
  font-size: 14px;
  font-weight: 700;
  letter-spacing: 0.02em;
  line-height: 1.2;
  margin: 0 0 14px;
  text-transform: uppercase;
`;

export const LinkList = styled.ul`
  display: flex;
  flex-direction: column;
  gap: 10px;
  list-style: none;
  margin: 0;
  padding: 0;

  ${mobile()} {
    gap: 4px;
  }
`;

export const LinkColumns = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  column-gap: 28px;
  row-gap: 10px;
  width: 100%;

  @media (max-width: 760px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  ${mobile()} {
    grid-template-columns: 1fr;
  }
`;

const crawlableFooterLinkStyles = css`
  color: #dcddde;
  align-items: center;
  display: inline-flex;
  font-size: 14px;
  line-height: 20px;
  text-decoration: none;
  transition: color 150ms ease, text-decoration-color 150ms ease;

  &:visited {
    color: #dcddde;
  }

  &:hover,
  &:focus-visible {
    color: ${({ theme }) => theme.color.cta};
    text-decoration: underline;
    text-decoration-thickness: 1px;
    text-underline-offset: 3px;
  }

  ${mobile()} {
    min-height: 36px;
  }
`;

export const GridLink = styled(Link)`
  ${crawlableFooterLinkStyles};
`;

export const GridEmailLink = styled(EmailLink)`
  ${crawlableFooterLinkStyles};
`;

export const BrandRow = styled.div`
  ${pageWidthStyles};
  align-items: center;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  gap: 24px;
  justify-content: space-between;
  max-width: ${PAGE_CONTENT}px;
  order: 3;
  padding: 28px 0;

  ${mobile()} {
    align-items: flex-start;
    border-top: 0;
    flex-direction: column;
    gap: 18px;
    order: 1;
  }
`;

export const BrandBlock = styled.div`
  max-width: 620px;
`;

export const BrandLine = styled.p`
  color: ${({ theme }) => theme.color.textSecondary};
  font-size: 15px;
  line-height: 22px;
  margin: 14px 0 0;
`;

export const SocialLinks = styled.div`
  display: flex;
  gap: 10px;
`;

export const BrandLinkRow = styled.div`
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 12px 18px;
  margin-top: 16px;
`;

export const SocialLink = styled(Link)`
  align-items: center;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 6px;
  color: ${({ theme }) => theme.color.textPrimary};
  display: inline-flex;
  height: 36px;
  justify-content: center;
  width: 36px;

  &:visited {
    color: ${({ theme }) => theme.color.textPrimary};
  }

  &:hover,
  &:focus-visible {
    border-color: ${({ theme }) => theme.color.cta};
    color: ${({ theme }) => theme.color.cta};
  }

  svg {
    height: 18px;
    width: 18px;
  }

  ${mobile()} {
    height: 44px;
    width: 44px;
  }
`;

export const BrandEmailLink = styled(EmailLink)`
  ${crawlableFooterLinkStyles};
  gap: 8px;
  min-height: 36px;

  svg {
    flex-shrink: 0;
    height: 14px;
    width: 14px;
  }
`;

export const AccountLinks = styled.div`
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  justify-content: flex-end;

  ${mobile()} {
    justify-content: flex-start;
  }
`;

export const AccountTextLink = styled(Link)`
  ${crawlableFooterLinkStyles};
  align-items: center;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 6px;
  display: inline-flex;
  min-height: 40px;
  padding: 0 14px;
`;

export const AppLinks = styled.div`
  display: flex;
  flex-wrap: nowrap;
  gap: 10px;

  @media (max-width: 540px) {
    flex-direction: column;
    width: 100%;
  }
`;

export const AppDownloadLink = styled(Link)`
  align-items: center;
  background: #050505;
  border: 1px solid rgba(255, 255, 255, 0.18);
  border-radius: 6px;
  color: #ffffff;
  display: inline-flex;
  gap: 12px;
  min-height: 52px;
  min-width: 168px;
  padding: 0 16px;
  text-decoration: none;
  transition: border-color 150ms ease, background-color 150ms ease;

  &:visited {
    color: #ffffff;
  }

  &:hover,
  &:focus-visible {
    background: #111111;
    border-color: ${({ theme }) => theme.color.cta};
    color: #ffffff;
  }

  svg {
    flex-shrink: 0;
    height: 24px;
    width: 24px;
  }

  @media (max-width: 540px) {
    width: 100%;
  }
`;

export const AppDownloadEyebrow = styled.span`
  display: block;
  font-size: 10px;
  font-weight: 700;
  line-height: 1;
`;

export const AppDownloadText = styled.span`
  display: block;
  font-size: 18px;
  font-weight: 800;
  line-height: 1.1;
`;

export const TrustBlock = styled.div`
  align-items: center;
  display: flex;
  gap: 22px;
  min-width: 0;

  ${mobile()} {
    align-items: flex-start;
    flex-direction: column;
  }
`;

export const AppBlock = styled(TrustBlock)`
  align-items: flex-start;
  flex-direction: column;
  gap: 10px;
  justify-self: end;

  h2 {
    margin-bottom: 0;
  }

  @media (max-width: 1080px) {
    justify-self: start;
  }
`;

export const TrustTitle = styled.h2`
  color: ${({ theme }) => theme.color.textPrimary};
  font-size: 16px;
  font-weight: 800;
  line-height: 1.2;
  margin: 0 0 6px;
  text-transform: uppercase;
`;

export const TrustText = styled.p`
  color: ${({ theme }) => theme.color.textSecondary};
  font-size: 13px;
  line-height: 18px;
  margin: 0;
`;

export const AwardBadges = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

export const AwardBadge = styled.img`
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  height: 58px;
  object-fit: contain;
  padding: 4px;
  width: 58px;
`;

export const BuyNowLink = styled(Link)`
  align-items: center;
  background-color: ${({ theme }) => theme.color.cta};
  border: 2px solid ${({ theme }) => theme.color.cta};
  border-radius: 4px;
  color: ${({ theme }) => theme.color.textPrimary};
  display: inline-flex;
  font-size: 16px;
  font-weight: 700;
  height: 52px;
  justify-content: center;
  min-width: 200px;
  text-decoration: none;
  transition: background-color 0.2s ease, border-color 0.2s ease, color 0.2s ease;

  &:visited {
    color: ${({ theme }) => theme.color.textPrimary};
  }

  &:hover,
  &:focus-visible {
    background-color: ${({ theme }) => theme.color.textPrimary};
    border-color: ${({ theme }) => theme.color.textPrimary};
    color: ${({ theme }) => theme.color.cta};
  }
`;

export const UtilityLinks = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px 18px;
  justify-content: flex-end;

  ${mobile()} {
    justify-content: flex-start;
  }
`;

export const UtilityLink = styled(Link)`
  ${crawlableFooterLinkStyles};
  font-size: 13px;
`;

export const LegalBlock = styled.div`
  width: 100%;
`;

export const LegalTopRow = styled.div`
  align-items: center;
  display: flex;
  gap: 20px;
  justify-content: space-between;
  margin-bottom: 12px;

  ${mobile()} {
    align-items: flex-start;
    flex-direction: column;
    gap: 10px;
  }
`;
